import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "utils/api";
import { useAuth } from "context/AuthContext";
import Pagination from "components/pagination";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { Search } from "lucide-react";

const DEFAULT_MAP_CENTER = [20.5937, 78.9629];
const LIVE_THRESHOLD_MS = 30 * 60 * 1000; // 30 min — consider "Live" if location updated within this

function createRunnerMapIcon(name, isLive) {
  const safe = String(name || "Runner")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  const status = isLive ? "Live" : "Offline";
  const statusColor = isLive ? "#16a34a" : "#6b7280";
  return L.divIcon({
    className: "all-runners-marker",
    html: `<div class="all-runners-pin" style="cursor:pointer"><span class="all-runners-dot" style="background:${statusColor}"></span><span class="all-runners-name">${safe}</span><span class="all-runners-status" style="color:${statusColor}">${status}</span></div>`,
    iconSize: [64, 44],
    iconAnchor: [32, 40],
  });
}

function FitRunnersBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    if (positions.length === 1) {
      map.setView(positions[0], 14);
      return;
    }
    const bounds = L.latLngBounds(positions);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [map, positions]);
  return null;
}

// Helper component for details
const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b py-1 text-sm">
    <span className="font-medium">{label}</span>
    <span>{value}</span>
  </div>
);

// Modal wrapper
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50">
    <div className="w-[95%] max-w-md rounded-xl bg-white p-4 sm:w-full sm:p-6">
      <h3 className="mb-4 text-lg font-bold">{title}</h3>
      {children}
    </div>
  </div>
);

export default function UsersTable() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile_no: "",
    password: "",
    role: "",
    status: 1,
    user_id: null,
  });

  const [editIndex, setEditIndex] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [runnerLocations, setRunnerLocations] = useState({});
  const isEdit = editIndex != null;

  const USERS_PAGE_SIZE = 10;
  const [usersPage, setUsersPage] = useState(1);
  const filteredUsers = (users || []).filter((user) => {
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term) ||
      (user.mobile_no + "").includes(term) ||
      user.role?.toLowerCase().includes(term) ||
      (user.status === 1 ? "active" : "inactive").includes(term)
    );
  });

  const usersTotalPages = Math.max(
    1,
    Math.ceil(filteredUsers.length / USERS_PAGE_SIZE)
  );

  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * USERS_PAGE_SIZE,
    usersPage * USERS_PAGE_SIZE
  );

  // Load users from backend
  const loadUsers = async () => {
    try {
      const res = await api.post("/list_user", { role: "R" });
      setUsers(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (!users || users.length === 0) return;
    const fetchAll = async () => {
      const next = {};
      await Promise.all(
        users.map(async (u) => {
          try {
            const res = await api.get(`/admin/runner/${u.id}/location`);
            if (res.data?.latitude != null && res.data?.longitude != null) {
              next[u.id] = {
                lat: Number(res.data.latitude),
                lon: Number(res.data.longitude),
                last_updated: res.data.last_updated,
              };
            }
          } catch {
            // no location
          }
        })
      );
      setRunnerLocations(next);
    };
    fetchAll();
    const interval = setInterval(fetchAll, 20000);
    return () => clearInterval(interval);
  }, [users]);

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      mobile_no: "",
      password: "",
      role: "R",
      status: 1,
      user_id: null,
    });
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit form (create or edit)

  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      mobile_no: user.mobile_no,
      role: user.role,
      password: "",
      status: user.status,
      user_id: user.id,
    });
    setEditIndex(user.id);
    setShowModal(true);
  };

  const handleView = (user) => {
    setSelectedUser(user);
  };

  const handleChangeStatus = async (user, status) => {
    try {
      await api.post("/edit_user_status", {
        user_id: user.id,
        status,
        admin_id: userId,
      });
      toast.success("User status updated");
      loadUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEdit) {
        await api.post("/edit_user", {
          ...formData,
          admin_id: formData.user_id,
        });
        toast.success("User updated successfully");
      } else {
        await api.post("/create_user", {
          name: formData.name,
          email: formData.email,
          mobile_no: Number(formData.mobile_no),
          password: formData.password,
          role: formData.role,
          user_id: userId,
        });
        toast.success("User created successfully");
      }
      resetForm();
      setShowModal(false);
      loadUsers();
    } catch (err) {
      console.error(err);
      const d = err.response?.data?.detail;
      const msg =
        typeof d === "string"
          ? d
          : Array.isArray(d)
          ? d.map((x) => x.msg).join(" ")
          : d?.msg;
      toast.error(msg || "Failed to submit");
    }
  };

  const mapPositions = Object.entries(runnerLocations).map(([, v]) => [
    v.lat,
    v.lon,
  ]);
  const hasMapPositions = mapPositions.length > 0;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* All runners map */}
      <div className="ring-black/5 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg ring-1 dark:border-white/10 dark:bg-navy-800">
        <style>{`
          .all-runners-marker { background: none !important; border: none !important; }
          .all-runners-pin { display: flex; flex-direction: column; align-items: center; }
          .all-runners-dot { width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); }
          .all-runners-name { font-size: 11px; font-weight: 700; color: #111; background: rgba(255,255,255,0.95); padding: 2px 6px; border-radius: 6px; margin-top: 2px; white-space: nowrap; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
          .all-runners-status { font-size: 9px; font-weight: 600; text-transform: uppercase; margin-top: 1px; }
        `}</style>
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/50 px-4 py-3 dark:border-white/10 dark:from-white/5 dark:to-blue-500/10">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            All runners on map
          </span>
          <span className="text-xs text-gray-500">
            Click a runner to open their page · refreshes every 20s
          </span>
        </div>
        <div className="h-[220px] w-full sm:h-[280px] md:h-[320px]">
          <MapContainer
            center={DEFAULT_MAP_CENTER}
            zoom={hasMapPositions ? 5 : 4}
            style={{ height: "100%", width: "100%" }}
            className="z-0 rounded-b-2xl"
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution="&copy; OpenStreetMap &copy; CartoDB"
            />
            {Object.entries(runnerLocations).map(([runnerId, loc]) => {
              const user = users.find((u) => String(u.id) === String(runnerId));
              const name = user?.name || `Runner ${runnerId}`;
              let isLive = true;
              if (loc.last_updated) {
                const updatedAt = new Date(loc.last_updated).getTime();
                isLive =
                  !Number.isNaN(updatedAt) &&
                  Date.now() - updatedAt < LIVE_THRESHOLD_MS;
              }
              return (
                <Marker
                  key={runnerId}
                  position={[loc.lat, loc.lon]}
                  icon={createRunnerMapIcon(name, isLive)}
                  eventHandlers={{
                    click: () => navigate(`/admin/runner_details/${runnerId}`),
                  }}
                />
              );
            })}
            {hasMapPositions && <FitRunnersBounds positions={mapPositions} />}
          </MapContainer>
        </div>
      </div>

      {/* Header */}
      <div className="rounded-xl bg-white p-6 shadow-md transition-shadow duration-300 hover:shadow-xl">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Runners
          </h4>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search runners..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setUsersPage(1);
                }}
                className="w-full rounded-lg border px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              />
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-2 text-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <span>Add Runner</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b text-center">
                <th className="py-5">Name</th>
                <th className="py-5">Email</th>
                <th className="py-5">Phone</th>
                <th className="py-2">Role</th>
                <th className="py-2">Status</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-4 text-center text-gray-400">
                    No users added
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, index) => (
                  <tr
                    key={user.id || index}
                    className="border-b text-center hover:bg-gray-50"
                  >
                    <td className="py-5">{user.name}</td>
                    <td className="py-2">{user.email}</td>
                    <td className="py-2">{user.mobile_no}</td>
                    <td className="py-2">
                      {user.role === "R" ? "Runner Boy" : user.role}
                    </td>

                    <td
                      className={`py-2 capitalize ${
                        user.status === 1
                          ? "font-semibold text-green-600"
                          : "font-semibold text-red-600"
                      }`}
                    >
                      {user.status === 1 ? "Active" : "Inactive"}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          onClick={() =>
                            navigate(`/admin/runner_details/${user.id}`)
                          }
                          className="rounded-md bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300"
                        >
                          View
                        </button>
                        <button
                          onClick={() =>
                            navigate(`/admin/edit_runner/${user.id}`)
                          }
                          className="rounded-md bg-indigo-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            handleChangeStatus(user, user.status === 1 ? 2 : 1)
                          }
                          className={`rounded-md px-3 py-1 text-xs font-medium ${
                            user.status === 1
                              ? "bg-red-500 text-white"
                              : "bg-green-500 text-white"
                          } transition hover:opacity-80`}
                        >
                          {user.status === 1 ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={usersPage}
          totalPages={usersTotalPages}
          totalItems={filteredUsers.length || 0}
          pageSize={USERS_PAGE_SIZE}
          onPageChange={setUsersPage}
          itemName="runners"
        />

        {/* Add/Edit Modal */}
        {showModal && (
          <Modal
            title={isEdit ? "Edit User" : "Add Runner"}
            onClose={() => setShowModal(false)}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create password"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required={!isEdit}
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase text-gray-900">
                  Phone
                </label>
                <input
                  type="text"
                  name="mobile_no"
                  value={formData.mobile_no}
                  onChange={handleChange}
                  placeholder="Enter your mobile no"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-700
              transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input type="hidden" name="role" value="R"></input>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="rounded-lg border px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-gradient-to-br from-[#F0F9FF] via-[#E0F2FE] to-[#7DD3FC] px-4 py-2
            text-sm font-medium text-gray-800 shadow-sm transition hover:scale-[1.02]"
                >
                  {isEdit ? "Update User" : "Create User"}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}
